import { useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { XLg } from "react-bootstrap-icons";
import type { ActionMeta, MultiValue } from "react-select";
import ReactSelect from "react-select";
import { DocumentSubType } from "@/briefpoint-client";
import type { Interrogatory } from "@/briefpoint-client/models/Interrogatory";
import { getSubTypeText } from "@/lib/utils";
import { Button } from "../ui/button";
import styles from "./InterrogatoriesComboBox.module.scss";

export interface InterrogatoryOption {
  label: React.ReactNode;
  value: number;
  interrogatory: Interrogatory;
}

interface Props {
  interrogatories: Interrogatory[];
  selected?: number[];
  onChange: (
    value: MultiValue<InterrogatoryOption>,
    action: ActionMeta<InterrogatoryOption>,
  ) => void;
  id: string;
  documentSubType?: DocumentSubType;
  jurisdictionId?: string;
}

export default function InterrogatoriesComboBox({
  interrogatories,
  selected,
  onChange,
  id,
  documentSubType,
  jurisdictionId,
}: Props) {
  const label = useMemo(() => {
    return getSubTypeText(
      documentSubType ?? DocumentSubType.RequestsForProduction,
      jurisdictionId ?? "",
    ).request.toUpperCase();
  }, [documentSubType, jurisdictionId]);

  const options: InterrogatoryOption[] = useMemo(() => {
    return interrogatories.map((interrogatory) => ({
      value: interrogatory.id,
      label: `${label} NO. ${interrogatory.number} - ${interrogatory.text}`, // data to search by
      interrogatory, // map the interrogatory to the option so we can access all the interrogatory data
    }));
  }, [interrogatories, label]);

  const selectedOptions = useMemo(() => {
    if (!selected || selected.length === 0) {
      return [];
    }
    return options.filter((option) => selected.includes(option.value));
  }, [options, selected]);

  // state to control the menu open/close - need this to be able to close the menu when the done button is clicked
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  return (
    <div className={styles.tagContainer}>
      <ReactSelect
        menuIsOpen={menuIsOpen}
        hideSelectedOptions={false}
        id={id}
        options={options}
        value={selectedOptions.filter(
          (opt): opt is NonNullable<typeof opt> => opt !== null,
        )}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        onChange={onChange}
        placeholder="Select Requests..."
        menuPlacement="auto"
        isMulti={true}
        isSearchable={true}
        onMenuClose={() => {
          setMenuIsOpen(false);
        }}
        onMenuOpen={() => {
          setMenuIsOpen(true);
        }}
        // custom components to override the default react-select components
        components={{
          Option: (props) => {
            return (
              /* biome-ignore lint/a11y/useKeyWithClickEvents: ignore this to allow label to be clicked */
              <label
                className="w-100"
                onClick={() => {
                  // logic to handle checkbox selection/deselection
                  if (
                    selectedOptions.some(
                      (filter) => filter.value === props.data.value,
                    )
                  ) {
                    onChange(
                      selectedOptions.filter(
                        (option) => option.value !== props.data.value,
                      ),
                      {
                        action: "deselect-option",
                        option: props.data,
                      },
                    );
                  } else {
                    onChange([...selectedOptions, props.data], {
                      action: "select-option",
                      option: props.data,
                    });
                  }
                }}
                htmlFor={`interrogatory-${props.data.interrogatory.id}`}
              >
                <div
                  className={`${styles.optionContainer} ${
                    selectedOptions.some(
                      (filter) => filter.value === props.data.value,
                    )
                      ? styles.selectedOption
                      : ""
                  }`}
                >
                  <div className={styles.optionItem}>
                    <Form.Check
                      className="form-check-no-shadow"
                      type="checkbox"
                      checked={selectedOptions.some(
                        (filter) => filter.value === props.data.value,
                      )}
                      onChange={(e) => {
                        // similar to the logic in the onClick handler for the label but for the checkbox
                        if (e.currentTarget.checked) {
                          onChange([...selectedOptions, props.data], {
                            action: "select-option",
                            option: props.data,
                          });
                        } else {
                          onChange(
                            selectedOptions.filter(
                              (option) => option.value !== props.data.value,
                            ),
                            {
                              action: "deselect-option",
                              option: props.data,
                            },
                          );
                        }
                      }}
                      id={`interrogatory-${props.data.interrogatory.id}`}
                    />

                    <div className={styles.optionLabel}>
                      {label} NO. {props.data.interrogatory.number}
                    </div>
                  </div>
                  <div className={styles.optionText}>
                    {props.data.interrogatory.text}
                  </div>
                </div>
              </label>
            );
          },
          MenuList: (props) => {
            // custom menu list to include the done button
            return (
              <>
                <div className={styles.doneButtonContainer}>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      setMenuIsOpen(false);
                    }}
                  >
                    Done
                  </Button>
                </div>
                <div className={styles.menuList}>{props.children}</div>
              </>
            );
          },
          MultiValueContainer: (props) => {
            // custom chips to override the default react-select component
            return (
              <div className={styles.tag}>
                No. {props.data.interrogatory.number}
                <Button
                  variant="ghost"
                  onClick={() => {
                    // logic to handle chip removal
                    onChange(
                      selectedOptions.filter(
                        (option) => option.value !== props.data.value,
                      ),
                      {
                        action: "deselect-option",
                        option: props.data,
                      },
                    );
                  }}
                  className={styles.closeButton}
                >
                  <XLg className={styles.closeIcon} />
                </Button>
              </div>
            );
          },
        }}
      />
    </div>
  );
}
